/**
 * The C1 organization route table (design §5.9).
 *
 * `src/router/index.ts` is P9's, so this module declares its records and the
 * router spreads `ORG_C1_ROUTES` in beside the existing organization routes.
 * The prefix matches them (`/settings/organization/…`) because these screens are
 * the same job: setting one company up before anybody posts to it.
 *
 * Every route is gated on a **read** permission. Creating a location, abolishing
 * a position and waiving a filing obligation are all checked on the action, so
 * an Auditor holding only the read codes can open every screen here and change
 * nothing — which is what makes the audit possible.
 *
 * C17 (2026-10-25) removed the `/settings/organization/divisions` record and its
 * screen: FSD 1.2.2 Divisions (`/enterprise/divisions`) superseded it. The old
 * path is a redirect in `router/legacyRedirects.ts`, and must never be declared
 * here again — an earlier record with the same path would shadow the redirect.
 */

import type { RouteRecordRaw } from 'vue-router'

import { ORG_PERMISSIONS } from '@/modules/org/permissions'

export const ORG_C1_ROUTES: RouteRecordRaw[] = [
  {
    path: '/settings/organization/locations',
    name: 'org-locations',
    component: () => import('@/modules/org/LocationsView.vue'),
    meta: { titleKey: 'org.routeTitles.locations', permission: ORG_PERMISSIONS.locationRead },
  },
  {
    path: '/settings/organization/jobs',
    name: 'org-jobs',
    component: () => import('@/modules/org/JobsView.vue'),
    meta: { titleKey: 'org.routeTitles.jobs', permission: ORG_PERMISSIONS.jobRead },
  },
  {
    path: '/settings/organization/job-grades',
    name: 'org-job-grades',
    component: () => import('@/modules/org/GradesView.vue'),
    meta: { titleKey: 'org.routeTitles.grades', permission: ORG_PERMISSIONS.jobRead },
  },
  {
    path: '/settings/organization/positions',
    name: 'org-positions',
    component: () => import('@/modules/org/PositionsView.vue'),
    meta: { titleKey: 'org.routeTitles.positions', permission: ORG_PERMISSIONS.positionRead },
  },
  {
    path: '/settings/organization/designations',
    name: 'org-designations',
    component: () => import('@/modules/org/DesignationsView.vue'),
    meta: { titleKey: 'org.routeTitles.designations', permission: ORG_PERMISSIONS.designationRead },
  },
  {
    path: '/settings/organization/filing-calendar',
    name: 'org-filing-calendar',
    component: () => import('@/modules/org/FilingCalendarView.vue'),
    meta: { titleKey: 'org.routeTitles.filings', permission: ORG_PERMISSIONS.filingRead },
  },
]
